import { FORM_ERROR } from 'final-form';
import { update } from 'lodash/fp';
import React, { useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import Reaptcha from 'reaptcha';

import { friendsAndFamilyMemberSelector } from '@skippay/core/modules/misc';

const EVENT_CAPTCHA_RESPONSE = `captchaResponse`;

/**
 * When there is problem in `captchaToken`, the server sets an `captchaToken` attribute error, but we have no such
 * attribute field in our forms so the error is nowhere to be rendered. Therefore, we move the captcha attribute error
 * message to the whole form `FORM_ERROR` error message.
 */
export const moveCaptchaAttributeErrorToFormError = (validationErrors?: {
	captchaToken?: string;
	[FORM_ERROR]?: string[];
	[key: string]: string | string[] | undefined;
}) => {
	if (!validationErrors) {
		return validationErrors;
	}
	const { captchaToken: captchaError, ...otherValidationErrors } = validationErrors;
	if (captchaError) {
		return update(
			[FORM_ERROR],
			(formError) => (formError || []).concat(captchaError),
			otherValidationErrors
		);
	}
	return validationErrors;
};

export default function useReCAPTCHA(): {
	/**
	 * Must be rendered into screen, otherwise reCAPTCHA wont work
	 */
	recaptchaNode: React.ReactNode;
	/**
	 * Returns a Promise of reCAPTCHA token or `undefined` when prerequisites are not met.
	 */
	getRecaptchaToken: () => Promise<string | undefined>;
} {
	const friendsAndFamilyMember = useSelector(friendsAndFamilyMemberSelector);
	const captchaInstance = useRef<Reaptcha>(null);
	// this is deferred value Indicating readiness of reCAPTCHA instance
	const [initialization] = useState(() => {
		let setReady;
		const initializedPromise = new Promise((resolve) => {
			setReady = resolve;
		});
		return { ready: initializedPromise, setReady };
	});
	// the value returned from the hook is all memoized
	return useMemo(() => {
		if (!friendsAndFamilyMember || !process.env.RECAPTCHA_SITE_KEY) {
			// currently the logic is dummy for non-friendsAndFamilyMember
			return { getRecaptchaToken: () => Promise.resolve(undefined), recaptchaNode: null };
		}
		// the API of reCAPTCHA is tricky and hard to convert into Promise(d) land (pun intended). The reCAPTCHA generates
		// just one token per session. During the first invocation, one must call `execute` on the Reaptcha instance and
		// the token is passed to the `onVerify` callback. Subsequent invocations of `execute` are no-op, the token can be
		// obtained only via `getResponse` method. The reCAPTCHA can also be reset which re-evaluates the user. This is
		// currently not implemented but I assume it would then work the same way, as with initial call - call `execute` and
		// observe token in `onVerify` handler. The observing of `onVerify` is the tricky part here, I needed some
		// communication channel between `onVerify` and `getRecaptchaToken` handler compatible with Promise API. Because
		// the `execute` could be invoked multiple times (after a `reset`), one can use either Observable or EventEmitter.
		// Because Observable is much more complicated and its composition features are not needed here, I choose the
		// EventEmitter, which could be constructed easily with native DOM Interface.
		const eventEmitter = new EventTarget();
		const waitForCaptchaResponse = (): Promise<string> =>
			new Promise((resolve) => {
				const handler = ({ detail }: CustomEvent<string>) => {
					eventEmitter.removeEventListener(EVENT_CAPTCHA_RESPONSE, handler);
					resolve(detail);
				};
				eventEmitter.addEventListener(EVENT_CAPTCHA_RESPONSE, handler);
			});
		const recaptchaNode = (
			<Reaptcha
				onVerify={(captchaToken) => {
					eventEmitter.dispatchEvent(
						new CustomEvent<string>(EVENT_CAPTCHA_RESPONSE, { detail: captchaToken })
					);
				}}
				onLoad={initialization.setReady}
				ref={captchaInstance}
				sitekey={process.env.RECAPTCHA_SITE_KEY}
				size="invisible"
			/>
		);
		const getRecaptchaToken = async () => {
			await initialization.ready;
			if (!captchaInstance.current) {
				throw new Error(
					`The reCAPTCHA component failed to initialize. Did you rendered the recaptcha instance into the DOM?`
				);
			}
			const alreadyStoredResponse = await captchaInstance.current.getResponse();
			if (alreadyStoredResponse) {
				return alreadyStoredResponse;
			}
			captchaInstance.current.execute();
			return waitForCaptchaResponse();
		};
		return { getRecaptchaToken, recaptchaNode };
	}, [friendsAndFamilyMember, initialization]);
}
